import {
  useFetch,
  type UseFetchOptions,
  type UseFetchReturn,
} from "@vueuse/core";
import { computed, type MaybeRef, type MaybeRefOrGetter, toValue } from "vue";

import { useConfigStore } from "@/stores/config.ts";
import type {
  ContentBaseItem,
  ContentDetailItem,
  ContentItemSearchResponse,
  FilterRule,
  RelatedContent,
  SortSpec,
  VersionInfoResponse,
} from "@/types/content.d.ts";
import type { rawFilterSet } from "@/types/ui.ts";
import {
  contentApiClient,
  relatedContentApiClient,
} from "@/utils/apiClients.ts";

export interface HighlightSpec {
  field: string;
}
export interface SearchConfig {
  maxResults?: number;
  filterRules?: FilterRule[];
  searchContinuationFields?: { [key: string]: any }[];
  sortSpecs?: SortSpec[];
  requestSearchUpdate?: boolean;
  highlightSpecs?: HighlightSpec[];
  fullView?: boolean;
  offset?: number;
  savedSearchId?: string | null;
}

function useSearchResults<T extends ContentBaseItem = ContentBaseItem>(
  searchConfig: MaybeRef<SearchConfig>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<ContentItemSearchResponse<T>> {
  const configStore = useConfigStore();
  const SEARCH_CONFIG_DEFAULTS: SearchConfig = {
    maxResults: configStore.localConfig.search.maxResults,
    requestSearchUpdate: false,
    fullView: false,
  };
  return contentApiClient("search", options)
    .post(() => {
      const config = toValue(searchConfig);
      return {
        maxResults: config.maxResults ?? SEARCH_CONFIG_DEFAULTS.maxResults,
        filterRules: config.filterRules,
        searchContinuationFields: config.searchContinuationFields,
        sortSpecs: config.sortSpecs,
        requestSearchUpdate:
          config.requestSearchUpdate ??
          SEARCH_CONFIG_DEFAULTS.requestSearchUpdate,
        highlightSpecs: config.highlightSpecs ?? [],
        fullView: config.fullView ?? SEARCH_CONFIG_DEFAULTS.fullView,
      };
    })
    .json();
}

function useFilterTermSets(
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<rawFilterSet> {
  return contentApiClient("filter-term-sets", options).json();
}

function useSingleContentByUrn(
  urn: MaybeRefOrGetter<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<ContentDetailItem> {
  return contentApiClient(urn, options).json();
}

function useContentItemByReferer(
  referrerUrn: MaybeRefOrGetter<string | null>,
  targetUrn: MaybeRefOrGetter<string>,
  options: UseFetchOptions = {
    immediate: false,
  },
): UseFetchReturn<ContentDetailItem> {
  const url = computed(() => {
    // If no referrerUrn is provided a get to the targetUrn is done which is the same as the useSingleContentByUrn call
    if (!toValue(referrerUrn)) {
      return toValue(targetUrn);
    }
    return `${toValue(referrerUrn)}/${toValue(targetUrn)}`;
  });
  return contentApiClient(url, options).json();
}

function useMultipleContentByUrn(
  urns: MaybeRefOrGetter<string[]>,
  parentUrn: MaybeRefOrGetter<string | null>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
  sortSpecs?: SortSpec[],
): UseFetchReturn<ContentDetailItem[]> {
  const request = computed(() => ({
    requests: toValue(urns).map((urn) => ({ urn })),
    sortSpecs: sortSpecs ? sortSpecs : [],
    renditionSpec: {
      renditionTypesToBeSigned: ["thumbnail", "masonry"],
    },
    referrerUrn: toValue(parentUrn) ?? null,
  }));

  return contentApiClient("details", options).post(request).json();
}

function useVersionInfoByUrn(
  urn: MaybeRefOrGetter<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<VersionInfoResponse> {
  return contentApiClient("versioninfo", options)
    .post(() => ({
      urn: toValue(urn),
    }))
    .json();
}

function useAssetByUrl(
  url: MaybeRef<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<string> {
  return contentApiClient(url, options).json();
}

function useBlob(
  urn: MaybeRef<string>,
  type: MaybeRef<"text" | "graphic" | "video-original" | "referred-pictures">,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<Blob> {
  const url = computed(() => `${toValue(urn)}/download?type=${toValue(type)}`);
  return contentApiClient(url, options).blob();
}

function useBulkDownloadGetBlob(
  id: MaybeRefOrGetter<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<Blob> {
  const url = computed(() => `bulk-download/${toValue(id)}`);
  return contentApiClient(url, options).blob();
}

function useCreateBulkDownloadUuid(
  urns: MaybeRefOrGetter<string[]>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<string> {
  return contentApiClient("bulk-download", options).post(toValue(urns)).json();
}

function useTextBlobByUrn(
  urn: MaybeRef<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<Blob> {
  return useBlob(urn, "text", options);
}

function useGraphicsBlobByUrn(
  urn: MaybeRef<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<Blob> {
  return useBlob(urn, "graphic", options);
}

function useReferredPicturesBlobByUrn(
  urn: MaybeRef<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<Blob> {
  return useBlob(urn, "referred-pictures", options);
}

function useAssetBlobByPreSignedUrl(
  urn: MaybeRef<string>,
  options: UseFetchOptions = {
    immediate: false,
  },
): UseFetchReturn<Blob> {
  return useFetch(urn, options).blob();
}

function useRelatedContent(
  urn: MaybeRefOrGetter<string>,
  limit: MaybeRef<number>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<RelatedContent> {
  const url = `${toValue(urn)}?limit=${toValue(limit)}`;
  return relatedContentApiClient(url, options).json();
}

export {
  useAssetBlobByPreSignedUrl,
  useAssetByUrl,
  useBlob,
  useBulkDownloadGetBlob,
  useContentItemByReferer,
  useCreateBulkDownloadUuid,
  useFilterTermSets,
  useGraphicsBlobByUrn,
  useMultipleContentByUrn,
  useReferredPicturesBlobByUrn,
  useRelatedContent,
  useSearchResults,
  useSingleContentByUrn,
  useTextBlobByUrn,
  useVersionInfoByUrn,
};
